
import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { ptBR } from "date-fns/locale"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTimezone } from "@/contexts/TimezoneContext"

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Selecione uma data",
  disabled = false,
  className
}: DatePickerProps) {
  const { getCurrentDateInUserTimezone, isTimezoneReady } = useTimezone();
  const [open, setOpen] = React.useState(false);
  const [tempDate, setTempDate] = React.useState<Date | undefined>(date);

  // Sync tempDate with external date changes
  React.useEffect(() => {
    setTempDate(date);
  }, [date]);

  // Use user's current date as default if no date is provided
  const getDefaultDate = React.useCallback(() => {
    if (!isTimezoneReady) return undefined;
    
    const todayString = getCurrentDateInUserTimezone();
    const [year, month, day] = todayString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [getCurrentDateInUserTimezone, isTimezoneReady]);

  const handleConfirm = () => {
    onDateChange?.(tempDate);
    setOpen(false);
  };

  const handleClear = () => {
    setTempDate(undefined);
  };

  const handleCancel = () => {
    setTempDate(date);
    setOpen(false);
  };

  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    setTempDate(selectedDate);
    // Immediately apply the date selection while keeping the calendar open
    onDateChange?.(selectedDate);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "dd/MM/yyyy", { locale: ptBR }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-background border-border" align="start">
        <Calendar
          mode="single"
          selected={tempDate}
          onSelect={handleCalendarSelect}
          initialFocus
          defaultMonth={tempDate || date || getDefaultDate()}
          className="bg-background text-foreground"
        />
        <div className="flex gap-2 p-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="flex-1 rounded-xl"
          >
            Cancelar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            className="flex-1 rounded-xl"
          >
            Limpar
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            className="flex-1 rounded-xl"
          >
            Confirmar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
